// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Modal confirmation dialogs for destructive flight + aircraft
// deletions. Each function shows the matching <dialog> element (or
// falls back to window.confirm when the dialog markup is missing),
// returns a Promise that resolves true on submit and false on cancel /
// close, and tears its listeners down before resolving.

export function confirmFlightDeleteDialog({
  title,
  eyebrow = "Permanent Delete",
  copy,
  details,
  warningTitle = null,
  confirmLabel,
  destructive = true,
  showRetentionDisclosure = false,
  showAiSummaryOption = false,
  fileName = null,
}) {
  const dialog = document.querySelector("#flightDeleteDialog");
  const form = document.querySelector("#flightDeleteDialogForm");
  const eyebrowEl = document.querySelector("#flightDeleteDialogEyebrow");
  const titleEl = document.querySelector("#flightDeleteDialogTitle");
  const copyEl = document.querySelector("#flightDeleteDialogCopy");
  const warningTitleEl = document.querySelector("#flightDeleteDialogWarningTitle");
  const detailsEl = document.querySelector("#flightDeleteDialogDetails");
  const cancelButton = document.querySelector("#flightDeleteDialogCancel");
  const closeButton = document.querySelector("#flightDeleteDialogClose");
  const confirmButton = document.querySelector("#flightDeleteDialogConfirm");
  if (!dialog || !form) {
    return Promise.resolve(window.confirm([title, copy, details].filter(Boolean).join("\n\n")));
  }
  if (eyebrowEl) eyebrowEl.textContent = eyebrow;
  if (titleEl) titleEl.textContent = title;
  if (copyEl) copyEl.textContent = copy;
  if (warningTitleEl) warningTitleEl.textContent = warningTitle ?? (destructive ? "This cannot be undone." : "This only affects this list view.");
  if (detailsEl) detailsEl.textContent = details;
  // "File: <name>" line — identifies the flight when the title doesn't.
  const fileEl = document.querySelector("#flightDeleteDialogFile");
  if (fileEl) {
    fileEl.textContent = fileName ? `File: ${fileName}` : "";
    fileEl.hidden = !fileName;
  }
  const disclosureEl = document.querySelector("#flightDeleteDialogDisclosure");
  if (disclosureEl) disclosureEl.hidden = showRetentionDisclosure !== true;
  // Re-analyze variant only: "Generate AI summary" opt-in, always reset to
  // unchecked so a prior confirmation cannot silently spend AI allowance.
  const aiOptionEl = document.querySelector("#flightDeleteDialogAiOption");
  const aiOptionInput = document.querySelector("#flightDeleteDialogAiOptionInput");
  if (aiOptionEl) aiOptionEl.hidden = showAiSummaryOption !== true;
  if (aiOptionInput) aiOptionInput.checked = false;
  if (confirmButton) {
    confirmButton.textContent = confirmLabel;
    confirmButton.classList.toggle("danger-action", destructive);
  }
  const previouslyFocused = document.activeElement;
  return new Promise((resolve) => {
    let settled = false;
    const settle = (confirmed) => {
      if (settled) return;
      settled = true;
      form.removeEventListener("submit", onSubmit);
      cancelButton?.removeEventListener("click", onCancel);
      closeButton?.removeEventListener("click", onCancel);
      dialog.removeEventListener("cancel", onCancel);
      dialog.removeEventListener("close", onClose);
      if (dialog.open) {
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
      }
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus({ preventScroll: true });
      }
      resolve(confirmed);
    };
    const onSubmit = (event) => {
      event.preventDefault();
      settle(true);
    };
    const onCancel = (event) => {
      event?.preventDefault?.();
      settle(false);
    };
    const onClose = () => {
      settle(false);
    };
    form.addEventListener("submit", onSubmit);
    cancelButton?.addEventListener("click", onCancel);
    closeButton?.addEventListener("click", onCancel);
    dialog.addEventListener("cancel", onCancel);
    dialog.addEventListener("close", onClose);
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  });
}

// Resolves false when cancelled, or { confirmed: true, generateAiSummary }
// when confirmed — truthy either way so boolean callers keep working.
export async function confirmFlightReanalyzeDialog({ title, entry = null, upToDate = false }) {
  // An already-current flight can still be re-processed — the button no longer
  // blocks it — but the dialog has to say so plainly, or the action reads as a
  // no-op the user triggered by mistake. Same destructive-to-the-AI-summary
  // consequences either way, so the warning copy is shared. The title carries
  // no flight label; the File line identifies the flight instead.
  const fileName = entry?.original_filename
    ?? (entry?.path ? String(entry.path).split("/").pop() : null);
  const confirmed = await confirmFlightDeleteDialog({
    title,
    eyebrow: upToDate ? "Force Re-process" : "Update Flight",
    copy: upToDate
      ? "This analysis is already up to date. Re-processing rebuilds it from scratch and replaces the current results."
      : "This replaces the flight's current analysis results.",
    details: upToDate
      ? "Use this if you suspect the stored results are wrong. The current AI Summary will no longer match."
      : "The current AI Summary will no longer match until you generate a new one.",
    warningTitle: upToDate
      ? "Already up to date."
      : "Current AI Summary will be out of date.",
    confirmLabel: upToDate ? "Force Re-process" : "Update Flight",
    destructive: false,
    showAiSummaryOption: true,
    fileName,
  });
  if (!confirmed) return false;
  const generateAiSummary = document.querySelector("#flightDeleteDialogAiOptionInput")?.checked === true;
  return { confirmed: true, generateAiSummary };
}

export function confirmFlightsUpdateDialog({ count = 0, reprocessCount = 0, reanalyzeCount = 0 } = {}) {
  const plural = count === 1 ? "" : "s";
  const details = [
    reprocessCount > 0
      ? `${reprocessCount} selected flight${reprocessCount === 1 ? "" : "s"} will be rebuilt from ${reprocessCount === 1 ? "its" : "their"} original upload${reprocessCount === 1 ? "" : "s"}.`
      : "",
    reanalyzeCount > 0
      ? `${reanalyzeCount} selected flight${reanalyzeCount === 1 ? "" : "s"} will have ${reanalyzeCount === 1 ? "its" : "their"} analysis refreshed from saved flight data.`
      : "",
  ].filter(Boolean).join(" ");
  return confirmFlightDeleteDialog({
    title: `Update ${count} selected flight${plural}?`,
    eyebrow: "Update Selected Flights",
    copy: "AlaryIQ will apply the right update to each selected flight. You can keep using the app while this runs.",
    details,
    warningTitle: "Current AI Summaries will be out of date.",
    confirmLabel: `Update ${count} Selected Flight${plural}`,
    destructive: false,
  });
}

export function confirmFlightsReanalyzeAllDialog({ count = 0, scope = "all" } = {}) {
  const plural = count === 1 ? "" : "s";
  const selected = scope === "selected";
  return confirmFlightDeleteDialog({
    title: selected ? `Re-analyze ${count} selected flight${plural}?` : `Re-analyze all ${count} flight${plural}?`,
    eyebrow: selected ? "Re-analyze Selected Flights" : "Re-analyze All Flights",
    copy: selected
      ? "Re-analyzing will replace the current analysis results for the selected supported flights. Flights are processed one at a time and you can keep using the app while this runs."
      : "Re-analyzing will replace the current analysis results for every supported flight on this aircraft. Flights are processed one at a time and you can keep using the app while this runs.",
    details: "After re-analysis finishes, you can generate new AI Summaries for the updated results.",
    warningTitle: "Current AI Summaries will be out of date.",
    confirmLabel: selected ? `Re-analyze ${count} Selected Flight${plural}` : `Re-analyze ${count} Flight${plural}`,
    destructive: false,
  });
}

// Plan-limit stop shown when the plan has no room for the attempted action:
// before an add flow opens (context "add") or when a restore from the
// Archive tab asks for room that isn't there (context "restore"). Resolves
// "plans" (View Plans) or null (dismissed); it never performs the action.
export function showPlanLimitDialog({ scope = "flight", limit = null, planLabel = null, context = "add", message = null } = {}) {
  const dialog = document.querySelector("#planLimitDialog");
  const form = document.querySelector("#planLimitDialogForm");
  const titleEl = document.querySelector("#planLimitDialogTitle");
  const copyEl = document.querySelector("#planLimitDialogCopy");
  const dismissButton = document.querySelector("#planLimitDialogDismiss");
  const closeButton = document.querySelector("#planLimitDialogClose");
  const noun = scope === "aircraft" ? "aircraft" : "flight";
  const limitText = limit != null ? ` (${limit})` : "";
  const planText = planLabel ? `${planLabel} plan` : "plan";
  const title = `You're at your ${planText}'s ${noun} limit`;
  const copy = message ?? (context === "restore"
    ? (scope === "aircraft"
      ? `Your ${planText} is at its aircraft limit${limitText}. Upgrade your plan to restore this aircraft.`
      : `Your ${planText} is at its flight limit${limitText}. Upgrade your plan to restore this flight.`)
    : (scope === "aircraft"
      ? `Your ${planText} is at its aircraft limit${limitText}. Upgrade your plan to add more aircraft.`
      : `Your ${planText} is at its flight limit${limitText}. Upgrade your plan to upload more flights.`));
  if (!dialog || !form) {
    window.alert(`${title}\n\n${copy}`);
    return Promise.resolve(null);
  }
  if (titleEl) titleEl.textContent = title;
  if (copyEl) copyEl.textContent = copy;
  const previouslyFocused = document.activeElement;
  return new Promise((resolve) => {
    let settled = false;
    const settle = (choice) => {
      if (settled) return;
      settled = true;
      form.removeEventListener("submit", onPlans);
      dismissButton?.removeEventListener("click", onDismiss);
      closeButton?.removeEventListener("click", onDismiss);
      dialog.removeEventListener("cancel", onDismiss);
      dialog.removeEventListener("close", onClose);
      if (dialog.open) {
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
      }
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus({ preventScroll: true });
      }
      resolve(choice);
    };
    const onPlans = (event) => {
      event.preventDefault();
      settle("plans");
    };
    const onDismiss = (event) => {
      event?.preventDefault?.();
      settle(null);
    };
    const onClose = () => {
      settle(null);
    };
    form.addEventListener("submit", onPlans);
    dismissButton?.addEventListener("click", onDismiss);
    closeButton?.addEventListener("click", onDismiss);
    dialog.addEventListener("cancel", onDismiss);
    dialog.addEventListener("close", onClose);
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  });
}

export function confirmAircraftDeleteDialog({ label, flightCount, archive = false, showRetentionDisclosure = false }) {
  const dialog = document.querySelector("#aircraftDeleteDialog");
  const form = document.querySelector("#aircraftDeleteDialogForm");
  const eyebrow = document.querySelector("#aircraftDeleteDialogEyebrow");
  const title = document.querySelector("#aircraftDeleteDialogTitle");
  const copy = document.querySelector("#aircraftDeleteDialogCopy");
  const warningTitle = document.querySelector("#aircraftDeleteDialogWarningTitle");
  const details = document.querySelector("#aircraftDeleteDialogDetails");
  const cancelButton = document.querySelector("#aircraftDeleteDialogCancel");
  const closeButton = document.querySelector("#aircraftDeleteDialogClose");
  const confirmButton = document.querySelector("#aircraftDeleteDialogConfirm");
  const flightsText = `${flightCount} flight${flightCount === 1 ? "" : "s"}`;
  if (!dialog || !form) {
    return Promise.resolve(window.confirm(archive
      ? `Archive ${label}?\n\nThe aircraft and its ${flightsText} move to the Archive tab in My Account. Nothing is deleted, and you can restore it whenever your plan has room.`
      : `Permanently delete ${label} and all associated data?\n\nThis removes the aircraft profile, ${flightsText}, database records, generated reports, analysis artifacts, and locally stored upload files. This cannot be undone.`));
  }
  if (eyebrow) eyebrow.textContent = archive ? "Archive Aircraft" : "Permanent Delete";
  if (title) title.textContent = archive ? `Archive ${label}?` : `Delete ${label}?`;
  if (copy) {
    copy.textContent = archive
      ? `This moves ${label} and its ${flightsText} to the Archive tab in My Account.`
      : `This will permanently delete ${label} and ${flightsText} from this workspace.`;
  }
  if (warningTitle) warningTitle.textContent = archive ? "Nothing is deleted." : "This cannot be undone.";
  if (details) {
    details.textContent = archive
      ? "Archived aircraft stop counting toward your plan limits and disappear from this workspace. Restore the aircraft from the Archive tab whenever your plan has room."
      : "The aircraft profile, flight logs, database records, generated reports, analysis artifacts, and locally stored upload files will be removed.";
  }
  if (confirmButton) {
    confirmButton.textContent = archive ? "Archive Aircraft" : "Delete Aircraft";
    confirmButton.classList.toggle("danger-action", !archive);
  }
  const disclosureEl = document.querySelector("#aircraftDeleteDialogDisclosure");
  if (disclosureEl) disclosureEl.hidden = archive || showRetentionDisclosure !== true;
  dialog.classList.toggle("is-archive-confirm", archive);
  return new Promise((resolve) => {
    let settled = false;
    const settle = (confirmed) => {
      if (settled) return;
      settled = true;
      form.removeEventListener("submit", onSubmit);
      cancelButton?.removeEventListener("click", onCancel);
      closeButton?.removeEventListener("click", onCancel);
      dialog.removeEventListener("cancel", onCancel);
      dialog.removeEventListener("close", onClose);
      if (dialog.open) {
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
      }
      resolve(confirmed);
    };
    const onSubmit = (event) => {
      event.preventDefault();
      settle(true);
    };
    const onCancel = (event) => {
      event?.preventDefault?.();
      settle(false);
    };
    const onClose = () => {
      settle(false);
    };
    form.addEventListener("submit", onSubmit);
    cancelButton?.addEventListener("click", onCancel);
    closeButton?.addEventListener("click", onCancel);
    dialog.addEventListener("cancel", onCancel);
    dialog.addEventListener("close", onClose);
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  });
}
